import {
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { Subscription } from 'rxjs';
import { LoggerService } from '../../../../../core/services/logger.service';
import { ButtonComponent } from '../../../../../shared/components/button/button.component';
import { IconComponent } from '../../../../../shared/components/icon/icon.component';
import { ModalDialogComponent } from '../../../../../shared/components/modal-dialog/modal-dialog.component';

/** Extended track capabilities including non-standard zoom property */
interface TrackCapabilitiesWithZoom extends MediaTrackCapabilities {
  zoom?: { min?: number; max?: number; step?: number };
}

/**
 * Formly field type for photo capture
 * Supports camera capture with preview and retake functionality
 * Stores photos as base64 data URLs
 * Supports multiple photos and custom filename prefixes
 */
@Component({
  selector: 'app-photo-field',
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    ModalDialogComponent,
    ButtonComponent,
    IconComponent,
  ],
  templateUrl: './photo-field.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './photo-field.component.css',
})
export class PhotoFieldComponent extends FieldType<FieldTypeConfig> implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  videoStream = signal<MediaStream | null>(null);
  isCameraActive = signal(false);
  photoDataUrls = signal<string[]>([]); // Changed to array for multiple photos
  error = signal<string | null>(null);

  // Zoom state
  zoomLevel = signal(1);
  minZoom = signal(1);
  maxZoom = signal(5);
  hardwareZoomSupported = signal(false);

  // Camera dialog state from URL param
  showCameraDialog = signal(false);
  private routeSubscription?: Subscription;

  // Pinch-to-zoom tracking
  private activePointers = new Map<number, PointerEvent>();
  private initialPinchDistance: number | null = null;
  private initialPinchZoom = 1;

  // Double-tap tracking
  private lastTapTime = 0;

  // Wheel event listener (registered with passive: false)
  private wheelListenerAbortController: AbortController | null = null;

  // Lazy logger for testability
  private _logger: ReturnType<LoggerService['getLogger']> | null = null;
  private get logger(): ReturnType<LoggerService['getLogger']> {
    if (!this._logger) {
      try {
        this._logger = inject(LoggerService).getLogger('photo-field');
      } catch {
        // In test environment without injection context, use window.console
        this._logger = {
          debug: window.console.log.bind(window.console),
          info: window.console.info.bind(window.console),
          warn: window.console.warn.bind(window.console),
          error: window.console.error.bind(window.console),
        } as ReturnType<LoggerService['getLogger']>;
      }
    }
    return this._logger;
  }

  // Computed properties for configuration
  allowMultiple = computed(() => this.props['allowMultiplePhotos'] === true);
  filenamePrefix = computed(() => {
    const prefix = this.props['filenamePrefix'];
    return typeof prefix === 'string' && prefix.length > 0
      ? prefix
      : this.props['label'] || 'photo';
  });

  zoomLabel = computed(() => {
    const z = this.zoomLevel();
    if (z <= 1) return '1×';
    return z.toFixed(1) + '×';
  });

  ngOnInit(): void {
    // Load existing photos if present
    const value = this.formControl.value;
    if (value) {
      // Support both single photo (string) and multiple photos (array)
      if (Array.isArray(value)) {
        this.photoDataUrls.set(value);
      } else if (typeof value === 'string') {
        this.photoDataUrls.set([value]);
      }
    }

    this.routeSubscription = this.route.queryParams.subscribe((params) => {
      const photoParam = params['photo'];

      // Open dialog if URL param matches this field
      if (photoParam === this.field.id) {
        this.showCameraDialog.set(true);
        // Start camera when dialog opens (including on page load)
        setTimeout(() => this.startCamera(), 0);
      } else {
        this.showCameraDialog.set(false);
      }
    });
  }

  ngOnDestroy(): void {
    this.stopCamera();
    this.routeSubscription?.unsubscribe();
  }

  openCameraDialog(): void {
    // Add URL param to trigger dialog; the route subscription starts the camera
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { photo: this.field.id },
      queryParamsHandling: 'merge',
    });
  }

  closeCameraDialog(): void {
    // Stop camera and remove URL param to close dialog
    this.stopCamera();
    const queryParams = { ...this.route.snapshot.queryParams };
    delete queryParams['photo'];
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: '',
    });
  }

  async startCamera(): Promise<void> {
    try {
      this.error.set(null);
      const stream = await window.navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment', // Use back camera on mobile
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      });

      this.videoStream.set(stream);
      this.isCameraActive.set(true);

      // Check for hardware zoom support
      const [track] = stream.getVideoTracks();
      const capabilities = track.getCapabilities() as TrackCapabilitiesWithZoom;
      if (capabilities?.zoom) {
        this.hardwareZoomSupported.set(true);
        this.minZoom.set(capabilities.zoom.min ?? 1);
        this.maxZoom.set(capabilities.zoom.max ?? 5);
      }

      // Wait for video element to be available
      setTimeout(() => {
        const videoElement = window.document.getElementById(this.id + '-video') as HTMLVideoElement;
        if (videoElement && stream) {
          videoElement.srcObject = stream;
        }

        // Register wheel listener with passive: false so we can call preventDefault()
        const container = window.document.getElementById(this.id + '-video-container');
        if (container) {
          this.wheelListenerAbortController = new AbortController();
          container.addEventListener('wheel', (e) => this.onWheelZoom(e as WheelEvent), {
            passive: false,
            signal: this.wheelListenerAbortController.signal,
          });
        }
      }, 0);
    } catch (err) {
      this.logger.error('Error accessing camera:', err);
      this.error.set('Unable to access camera. Please check permissions.');
    }
  }

  capturePhoto(): void {
    const videoElement = window.document.getElementById(this.id + '-video') as HTMLVideoElement;
    if (!videoElement) return;

    const zoom = this.zoomLevel();
    const vw = videoElement.videoWidth;
    const vh = videoElement.videoHeight;

    // Centre-crop the frame at the current zoom level — sharp crop, not upscaled
    const cropW = vw / zoom;
    const cropH = vh / zoom;
    const cropX = (vw - cropW) / 2;
    const cropY = (vh - cropH) / 2;

    const canvas = window.document.createElement('canvas');
    canvas.width = Math.round(cropW);
    canvas.height = Math.round(cropH);

    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(videoElement, cropX, cropY, cropW, cropH, 0, 0, canvas.width, canvas.height);

      // Convert to JPEG with 85% quality for reasonable file size
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85);

      // Add to photos array
      const currentPhotos = this.photoDataUrls();
      const updatedPhotos = [...currentPhotos, dataUrl];
      this.photoDataUrls.set(updatedPhotos);

      // Update form control value based on multiple/single mode
      if (this.allowMultiple()) {
        this.formControl.setValue(updatedPhotos);
      } else {
        // Single photo mode - just store the one photo
        this.formControl.setValue(dataUrl);
      }
      this.formControl.markAsTouched();
    }

    this.closeCameraDialog();
  }

  retakePhoto(index: number): void {
    const currentPhotos = this.photoDataUrls();
    const updatedPhotos = currentPhotos.filter((_, i) => i !== index);
    this.photoDataUrls.set(updatedPhotos);

    // Update form control
    if (updatedPhotos.length === 0) {
      this.formControl.setValue(null);
    } else if (this.allowMultiple()) {
      this.formControl.setValue(updatedPhotos);
    } else {
      this.formControl.setValue(updatedPhotos[0]);
    }
    this.formControl.markAsTouched();

    // Open camera to take a new photo
    this.openCameraDialog();
  }

  removePhoto(index: number): void {
    const currentPhotos = this.photoDataUrls();
    const updatedPhotos = currentPhotos.filter((_, i) => i !== index);
    this.photoDataUrls.set(updatedPhotos);

    // Update form control
    if (updatedPhotos.length === 0) {
      this.formControl.setValue(null);
    } else if (this.allowMultiple()) {
      this.formControl.setValue(updatedPhotos);
    } else {
      this.formControl.setValue(updatedPhotos[0]);
    }
    this.formControl.markAsTouched();
  }

  stopCamera(): void {
    const stream = this.videoStream();
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      this.videoStream.set(null);
    }
    // Clear srcObject so the browser releases the camera hardware
    const videoElement = window.document.getElementById(
      this.id + '-video',
    ) as HTMLVideoElement | null;
    if (videoElement) {
      videoElement.srcObject = null;
    }
    this.isCameraActive.set(false);

    // Clean up zoom state
    this.wheelListenerAbortController?.abort();
    this.wheelListenerAbortController = null;
    this.zoomLevel.set(1);
    this.hardwareZoomSupported.set(false);
    this.minZoom.set(1);
    this.maxZoom.set(5);
    this.activePointers.clear();
    this.initialPinchDistance = null;
  }

  // ── Zoom methods ─────────────────────────────────────────────────────────────

  private clampZoom(level: number): number {
    return Math.min(Math.max(level, this.minZoom()), this.maxZoom());
  }

  setZoom(level: number): void {
    const clamped = this.clampZoom(level);
    this.zoomLevel.set(clamped);
    this.applyHardwareZoom(clamped);
  }

  resetZoom(): void {
    this.setZoom(1);
  }

  private async applyHardwareZoom(level: number): Promise<void> {
    const stream = this.videoStream();
    if (!stream || !this.hardwareZoomSupported()) return;
    const [track] = stream.getVideoTracks();
    try {
      await track.applyConstraints({ advanced: [{ zoom: level } as MediaTrackConstraintSet] });
    } catch {
      // ignore — hardware zoom constraints are not always writable after stream starts
    }
  }

  onPointerDown(event: PointerEvent): void {
    this.activePointers.set(event.pointerId, event);
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);

    if (this.activePointers.size === 2) {
      const [p1, p2] = [...this.activePointers.values()];
      this.initialPinchDistance = Math.hypot(p2.clientX - p1.clientX, p2.clientY - p1.clientY);
      this.initialPinchZoom = this.zoomLevel();
    }
  }

  onPointerMove(event: PointerEvent): void {
    if (!this.activePointers.has(event.pointerId)) return;
    this.activePointers.set(event.pointerId, event);

    if (this.activePointers.size === 2 && this.initialPinchDistance !== null) {
      event.preventDefault();
      const [p1, p2] = [...this.activePointers.values()];
      const currentDistance = Math.hypot(p2.clientX - p1.clientX, p2.clientY - p1.clientY);
      const scale = currentDistance / this.initialPinchDistance;
      this.setZoom(this.initialPinchZoom * scale);
    }
  }

  onPointerUp(event: PointerEvent): void {
    this.activePointers.delete(event.pointerId);
    if (this.activePointers.size < 2) {
      this.initialPinchDistance = null;
    }
  }

  onPointerCancel(event: PointerEvent): void {
    this.activePointers.delete(event.pointerId);
    if (this.activePointers.size < 2) {
      this.initialPinchDistance = null;
    }
  }

  onWheelZoom(event: WheelEvent): void {
    event.preventDefault();
    const delta = event.deltaY * -0.005;
    this.setZoom(this.zoomLevel() + delta);
  }

  onVideoTap(): void {
    // Local double-tap gesture detection -- both reads are on this same device.
    // eslint-disable-next-line no-restricted-syntax
    const now = Date.now();
    if (now - this.lastTapTime < 300) {
      this.resetZoom();
    }
    this.lastTapTime = now;
  }

  // ── Upload from device ────────────────────────────────────────────────────────

  triggerUpload(): void {
    const input = window.document.getElementById(this.id + '-file-input') as HTMLInputElement;
    if (input) {
      input.value = ''; // reset so the same file can be re-selected
      input.click();
    }
  }

  async handleFileInput(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (!files || files.length === 0) return;

    try {
      const newUrls = await Promise.all(Array.from(files).map((f) => this.readFileAsDataUrl(f)));
      const currentPhotos = this.photoDataUrls();

      const updatedPhotos = this.allowMultiple() ? [...currentPhotos, ...newUrls] : [newUrls[0]]; // single mode: keep only the first selected file

      this.photoDataUrls.set(updatedPhotos);

      if (this.allowMultiple()) {
        this.formControl.setValue(updatedPhotos);
      } else {
        this.formControl.setValue(updatedPhotos[0]);
      }
      this.formControl.markAsTouched();
    } catch (err) {
      this.logger.error('Error reading uploaded file(s):', err);
      this.error.set('Unable to read the selected file. Please try another image.');
    }
  }

  private readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }

  // ── Photo management ──────────────────────────────────────────────────────────

  downloadPhoto(photoUrl: string, index: number): void {
    const link = window.document.createElement('a');
    link.href = photoUrl;

    // Generate filename with prefix and timestamp -- cosmetic, not app data.
    // eslint-disable-next-line no-restricted-syntax
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const prefix = this.filenamePrefix();
    const photoNumber = this.allowMultiple() ? `-${index + 1}` : '';
    link.download = `${prefix}${photoNumber}-${timestamp}.jpg`;

    link.click();
  }
}
